import {
  Alert,
  Col,
  Icon,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import { Formik } from "formik";
import React, { useCallback, useRef, useState } from "react";
import NumberFormat from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import { Creators as SupplementActions } from "../../../store/ducks/supplement";
import ButtonStandard from "../../utils/button";
import NoteIcon from "../../utils/icons/note";
import PencilIcon from "../../utils/icons/pencil";
import PlusBlueIcon from "../../utils/icons/plus/blue";
import TrashIcon from "../../utils/icons/trash";
import InfoTooltip from "../../utils/infoTooltip";
import SearchSupplement from "../../utils/searchSupplement";
import AlertIcon from "../supplement/icons/alert";
import SuccessIcon from "../supplement/icons/success";
import DrawerSupplementRestrictions from "../supplement/supplementRestrictions";
import DrawerSupplementSustenanceCapacity from "../supplement/supplementSustenanceCapacity";
import {
  Bold,
  Container,
  CustomDivider,
  Footer,
  RadioButtonCustom,
  RadioGroupCustom,
  StockItemSubsection,
  SupplementInfoCard,
  Table as TableCustomSupplement,
} from "./styles";
import {
  getSupplementShow,
  checkRecipeIngredients,
  saveSupplement,
} from "../../../services/supplementService";
import { getIndexIntegrationsFarm } from "../../../services/integrationPartnersService";
import { callStockItemIndex } from "../../../services/stockItemService";
import {
  findAllGroupItems,
  findAll as findAllItemsGroups,
} from "../../../services/itemsGroupService";
import { findAll as findAllUnits } from "../../../services/unitService";
import { numberMask } from "../../../services/helpersMethodsService";
import useWindowSize from "../../../hooks/useWindowSize";
import {
  computeFormulationNutrients,
  FORMULATION_NUTRIENTS,
  getTotalPercentAmount,
  isOriginalMatterFormulation,
  KG_MO,
  KG_MS_PER_BASE_KG_MS,
  PERCENT_KG_MS,
} from "../../../utils/formulationUtils";

const SUPPLEMENT_GROUPS = [{ id: 3, name: "Rações" }];

const SUPPLEMENT_SUB_GROUPS = [
  { id: 10, name: "S Ração", supplement_group_id: 3 },
];

const SUPPLEMENT_PRESENTATION = [
  { id: 1, name: "Gramínea" },
  { id: 2, name: "Silagem" },
  { id: 3, name: "Feno" },
  { id: 4, name: "Resíduo / Subproduto" },
  { id: 5, name: "Concentrado Seco" },
  { id: 6, name: "Concentrado Úmido" },
];

const RAW_MATERIAL_AMOUNT_UNIT = {
  percent_kg_ms: { label: "%/kgMS", value: "percent_kg_ms" },
  kg_ms_per_base_kg_ms: {
    label: "kg MS / X kg MS",
    value: "kg_ms_per_base_kg_ms",
  },
  kg_mo: { label: "kg MO", value: "kg_mo" },
};

const INITIAL_FORM_SUPPLEMENT_VALUES = {
  originalSupplementId: null,
  id: null,
  groupId: null,
  farmId: null,
  name: "",
  importCode: "",
  mixingTime: null,
  supplementFormulated: true,
  type: "Supplement",
  classification: "MineralEnergyProtein",
  industrialName: "",
  supplierId: null,
  cost: "",
  cp: "",
  cpUnit: "g_kg",
  tdn: "",
  tdnUnit: "g_kg",
  dryMatterPercentage: "",
  nitrogen: "",
  phosphorus: "",
  zinc: "",
  sodium: "",
  calcium: "",
  sulfur: "",
  potassium: "",
  potassiumUnit: "g_kg",
  nitrogenUnit: "g_kg",
  calciumUnit: "g_kg",
  phosphorusUnit: "g_kg",
  zincUnit: "g_kg",
  sodiumUnit: "g_kg",
  sulfurUnit: "g_kg",
  proportion: null,
  status: "Active",
  additiveEffect: null,
  additiveEffectUnit: "G_DAY",
  fda: "",
  lignin: "",
  ee: "",
  supplementGroupId: null,
  supplementSubGroupId: null,
  supplementPresentationId: null,
  fdn: "", // FDN
  pdr: "", // PDR/%PB
  pndr: "", // PNDR/%PB
  em: "", // EM (Mcal/kg)
  fef: "", // FEF %
  fdnfe: "", // FDNfe %
  farmRestrictions: null,
  formulateItems: [],
  observations: "",
  createStockItem: true,
  stockItemId: null,
  stockItemInternalId: "",
  stockItemAbbreviation: "",
  stockItemGroupItemId: null,
  stockItemItemGroupId: null,
  ingredientStockItemGroups: {},
  stockItemMeasurementUnitId: null,
};

const INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES = {
  showMix: false,
  minimumVoluntaryConsumption: "",
  maximumVoluntaryConsumption: "",
  voluntaryConsumptionType: "G_ANIMAL",
  minimumPure: "",
  minimumPureTrough: "",
  maximumPure: "",
  maximumPureTrough: "",
  pureConsumptionType: "G_ANIMAL",
  minimumMix: "",
  minimumMixTrough: "",
  maximumMix: "",
  maximumMixTrough: "",
  mixConsumptionType: "G_ANIMAL",
  mixRatio: "",
  status: "NI",
};

const INITIAL_FORM_FARM_RESTRICTIONS_VALUES = {
  reproductiveStatusList: [],
  minSupplementOfferedPerDay: null,
  maxSupplementOfferedPerDay: null,
  supplementOfferedUnitType: "PV_ANIMAL",
  maxDays: null,
  minMaleAgeMonths: null,
  maxMaleAgeMonths: null,
  minFemaleAgeMonths: null,
  maxFemaleAgeMonths: null,
  minCalfAgeMonths: null,
  maxCalfAgeMonths: null,
  minHeiferAgeMonths: null,
  maxHeiferAgeMonths: null,
  minMaleLiveWeight: null,
  maxMaleLiveWeight: null,
  minFemaleLiveWeight: null,
  maxFemaleLiveWeight: null,
  minCalfLiveWeight: null,
  maxCalfLiveWeight: null,
  minHeiferLiveWeight: null,
  maxHeiferLiveWeight: null,
  status: "NI",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  supplementGroupId: Yup.number().required(),
  supplementSubGroupId: Yup.number().required(),
  supplementPresentationId: Yup.number().required(),
  cost: Yup.number().min(0.001).required(),
  tdn: Yup.number().required(),
  cp: Yup.number().required(),
  dryMatterPercentage: Yup.number().min(0).max(100).required(),
});

const DECIMAL_SCALE_NUTRIENTS = 3;
const DECIMAL_SCALE_PRICE = 5;

function DrawerSupplementFormulated() {
  const size = useWindowSize();

  const [isLoadingRequestSaveOrUpdate, setIsLoadingRequestSaveOrUpdate] =
    useState(false);
  const [formSupplement, setFormSupplement] = useState(
    INITIAL_FORM_SUPPLEMENT_VALUES,
  );
  const [sustenanceCapacityData, setSustenanceCapacityData] = useState(
    INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES,
  );
  const [supplementFarmRestrictionsData, setSupplementFarmRestrictionsData] =
    useState(INITIAL_FORM_FARM_RESTRICTIONS_VALUES);
  const [
    drawerSupplementSustenanceCapacityVisible,
    setDrawerSupplementSustenanceCapacityVisible,
  ] = useState(false);
  const [
    drawerSupplementFarmRestrictionsVisible,
    setDrawerSupplementFarmRestrictionsVisible,
  ] = useState(false);
  const [isAutomacaoTratosEnabled, setIsAutomacaoTratosEnabled] =
    useState(false);
  const [unlinkedStockItems, setUnlinkedStockItems] = useState([]);
  const [hasPersistedStockItem, setHasPersistedStockItem] = useState(false);
  const [allStockItems, setAllStockItems] = useState([]);
  const [groupItems, setGroupItems] = useState([]);
  const [ingredientAnalysis, setIngredientAnalysis] = useState(null);
  const [itemsGroups, setItemsGroups] = useState([]);
  const [units, setUnits] = useState([]);

  const formik = useRef();

  const {
    app: {
      translation,
      groupSelected: { id: groupId },
      farmSelected: { id: farmId },
      groupSelected,
      farmSelected,
    },
    supplement: {
      drawerCreateEditSupplementFormulatedVisible,
      idSupplement,
      pagination: supplementPagination,
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  async function fetchDataSupplement() {
    setIsLoadingRequestSaveOrUpdate(true);
    try {
      const {
        data: { results },
      } = await getSupplementShow({
        groupId,
        farmId,
        id: idSupplement,
        isInputSupplier: false,
      });
      setHasPersistedStockItem(results.stockItemId != null);
      setFormSupplement({
        ...results,
        createStockItem: results.stockItemId == null && results.farmId != null,
        stockItemInternalId: "",
        stockItemAbbreviation: "",
        stockItemGroupItemId: null,
        stockItemItemGroupId: null,
        stockItemMeasurementUnitId: null,
        ingredientStockItemGroups: {},
      });
      setSustenanceCapacityData({
        showMix: results.showMix,
        minimumVoluntaryConsumption: results.minimumVoluntaryConsumption,
        maximumVoluntaryConsumption: results.maximumVoluntaryConsumption,
        voluntaryConsumptionType: results.voluntaryConsumptionType,
        minimumPure: results.minimumPure,
        minimumPureTrough: results.minimumPureTrough,
        maximumPure: results.maximumPure,
        maximumPureTrough: results.maximumPureTrough,
        pureConsumptionType: results.pureConsumptionType,
        minimumMix: results.minimumMix,
        minimumMixTrough: results.minimumMixTrough,
        maximumMix: results.maximumMix,
        maximumMixTrough: results.maximumMixTrough,
        mixConsumptionType: results.mixConsumptionType,
        mixRatio: results.mixRatio,
        status: "I",
      });
      setSupplementFarmRestrictionsData(
        results.restriction ?? INITIAL_FORM_FARM_RESTRICTIONS_VALUES,
      );
      analyzeIngredients(results.formulateItems);
    } catch (error) {
    } finally {
      setIsLoadingRequestSaveOrUpdate(false);
    }
  }

  async function loadStockItemData() {
    try {
      const [
        {
          data: { results: stockItems },
        },
        {
          data: { results: groupItemsResults },
        },
        {
          data: { results: itemsGroupsResults },
        },
        {
          data: { results: unitsResults },
        },
        {
          data: { results: allItems },
        },
      ] = await Promise.all([
        callStockItemIndex({ groupId, farmId, unlinkedItemsOnly: true }),
        findAllGroupItems({ groupId, farmId }),
        findAllItemsGroups({ groupId, farmId, size: 1000 }),
        findAllUnits({ groupId, farmId }),
        callStockItemIndex({ groupId, farmId }),
      ]);
      setUnlinkedStockItems(stockItems || []);
      setAllStockItems(allItems || []);
      setGroupItems(
        Array.isArray(groupItemsResults)
          ? groupItemsResults
          : (groupItemsResults?.content ?? []),
      );
      setItemsGroups(
        Array.isArray(itemsGroupsResults)
          ? itemsGroupsResults
          : (itemsGroupsResults?.content ?? []),
      );
      setUnits(unitsResults || []);
    } catch (error) {
      setUnlinkedStockItems([]);
      setAllStockItems([]);
      setGroupItems([]);
      setItemsGroups([]);
      setUnits([]);
    }
  }

  async function loadAutomacaoTratos() {
    try {
      const {
        data: { results },
      } = await getIndexIntegrationsFarm({ groupId, farmId });
      setIsAutomacaoTratosEnabled(
        (results || []).some(
          (i) =>
            i.status === "Active" &&
            i.integrationPartner?.partnerSystemName === "StarBras",
        ),
      );
    } catch (error) {
      setIsAutomacaoTratosEnabled(false);
    }
  }

  const closeDrawer = useCallback(() => {
    setHasPersistedStockItem(false);
    setFormSupplement(INITIAL_FORM_SUPPLEMENT_VALUES);
    setSustenanceCapacityData(INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES);
    setSupplementFarmRestrictionsData(INITIAL_FORM_FARM_RESTRICTIONS_VALUES);
    formik.current.resetForm(INITIAL_FORM_SUPPLEMENT_VALUES);
    dispatch(SupplementActions.showHideCreateEditSupplementFormulated());
  }, [dispatch]);

  const openNotificationError = (title, message) => {
    notification["error"]({
      message: title,
      description: message,
    });
  };

  const analyzeIngredients = useCallback(
    async (formulateItems) => {
      const ingredientIds = (formulateItems ?? [])
        .map((item) => item.supplementOrRawMaterialId)
        .filter((id) => id != null);

      if (ingredientIds.length === 0) {
        setIngredientAnalysis(null);
        return;
      }

      try {
        const {
          data: { results },
        } = await checkRecipeIngredients({ groupId, farmId, ingredientIds });
        setIngredientAnalysis(results);
      } catch (error) {
        console.error(error);
        setIngredientAnalysis(null);
      }
    },
    [groupId, farmId],
  );

  const farmIngredientsOf = (values) =>
    values.farmId == null ? [] : (ingredientAnalysis?.items ?? []);

  const ingredientsToCreateOf = (values) =>
    farmIngredientsOf(values).filter(
      (item) =>
        item.status === "NeedsStockItem" ||
        item.status === "NeedsCopyAndStockItem",
    );

  const ingredientsToRepointOf = (values) =>
    farmIngredientsOf(values).filter((item) => item.status === "NeedsRepoint");

  const ingredientNameOf = (item) =>
    item.ingredientName ?? translation.supplement.ingredientWithoutName;

  const groupItemNameOf = (groupItemId) =>
    groupItems.find((groupItem) => groupItem.id === groupItemId)?.name ?? "—";

  const itemGroupNameOf = (itemGroupId) =>
    itemsGroups.find((itemsGroup) => itemsGroup.id === itemGroupId)?.name ??
    "—";

  const setIngredientStockItemGroup = (
    ingredientId,
    field,
    value,
    values,
    setFieldValue,
  ) => {
    const current = values.ingredientStockItemGroups ?? {};
    const ingredient = current[ingredientId] ?? {};

    setFieldValue("ingredientStockItemGroups", {
      ...current,
      [ingredientId]: {
        ...ingredient,
        [field]: value,
        ...(field === "groupItemId" ? { itemGroupId: null } : {}),
      },
    });
  };

  const validateFormulateItems = (formulateItems) => {
    const errors = [];
    let totalAmount = 0;
    const isMO = isOriginalMatterFormulation(formulateItems);
    const percentItems = formulateItems.filter(
      (i) => i.amountUnit === PERCENT_KG_MS,
    );
    const addedItems = formulateItems.filter(
      (i) => i.amountUnit === KG_MS_PER_BASE_KG_MS,
    );
    if (formulateItems.length === 0) {
      errors.push(translation.supplement.errorNecessaryFormulatedItems);
    } else {
      formulateItems.forEach((item) => {
        if (item.supplementOrRawMaterialId == null) {
          errors.push(
            translation.supplement.errorFormulatedItemWithoutRawMaterial,
          );
        }
        if (isMO) {
          if (!(item.originalMatterAmount > 0)) {
            errors.push(
              translation.supplement
                .errorFormulatedItemWithoutOriginalMatterAmount,
            );
          }
        } else if (item.amountUnit === KG_MS_PER_BASE_KG_MS) {
          if (!(item.addedDryMatterAmount > 0)) {
            errors.push(
              translation.supplement.errorFormulatedItemWithoutAddedAmount,
            );
          }
          if (!(item.baseDryMatterAmount > 0)) {
            errors.push(
              translation.supplement.errorFormulatedItemWithoutBaseAmount,
            );
          }
        } else if (item.amount <= 0) {
          errors.push(translation.supplement.errorFormulatedItemWithoutAmount);
        } else {
          totalAmount += item.amount;
        }
      });
      if (
        !isMO &&
        percentItems.length > 0 &&
        Math.abs(totalAmount - 100) > 0.001
      ) {
        errors.push(
          `${translation.supplement.errorFormulatedItemTotalAmount} ${numberMask(
            totalAmount,
            false,
          )}%`,
        );
      }
      if (!isMO && addedItems.length > 0) {
        if (percentItems.length === 0) {
          errors.push(
            translation.supplement.errorFormulatedItemWithoutPercentItems,
          );
        } else {
          const lastPercentOrder = percentItems.reduce(
            (max, item) => Math.max(max, item.inclusionOrder || 0),
            0,
          );
          const hasInvalidOrder = addedItems.some(
            (item) => (item.inclusionOrder || 0) <= lastPercentOrder,
          );
          if (hasInvalidOrder) {
            errors.push(
              translation.supplement.errorFormulatedItemAddedInclusionOrder,
            );
          }
        }
      }
      const hasDuplicate = formulateItems.some(
        (item, index) =>
          item.supplementOrRawMaterialId &&
          formulateItems.findIndex(
            (i) =>
              i.supplementOrRawMaterialId === item.supplementOrRawMaterialId,
          ) !== index,
      );
      if (hasDuplicate) {
        errors.push(translation.supplement.errorFormulatedItemWithDuplicate);
      }
    }
    return new Set([...errors]);
  };

  const stockItemLabel = (stockItemId) => {
    if (stockItemId == null) {
      return "—";
    }
    const stockItem = allStockItems.find((item) => item.id === stockItemId);
    return stockItem
      ? `${stockItem.internalId} - ${stockItem.description}`
      : "—";
  };

  const validateStockItem = (values) => {
    const errors = [];
    const ingredientsToCreate = ingredientsToCreateOf(values);
    if (!hasPersistedStockItem && values.farmId != null) {
      if (values.createStockItem) {
        if (!values.stockItemInternalId || !values.stockItemInternalId.trim()) {
          errors.push(translation.supplement.errorStockItemWithoutInternalId);
        }
        if (!values.stockItemMeasurementUnitId) {
          errors.push(translation.supplement.errorStockItemWithoutUnit);
        }
        if (!values.stockItemGroupItemId || !values.stockItemItemGroupId) {
          errors.push(translation.supplement.errorStockItemWithoutGroup);
        }
      } else if (values.stockItemId == null) {
        errors.push(translation.supplement.errorStockItemNotSelected);
      }
    }
    if (ingredientsToCreate.length > 0) {
      const withoutGroup = ingredientsToCreate.filter((item) => {
        const group = values.ingredientStockItemGroups?.[item.ingredientId];
        return !group || !group.groupItemId || !group.itemGroupId;
      });

      if (withoutGroup.length > 0) {
        errors.push(
          translation.supplement.errorIngredientStockItemWithoutGroup.replace(
            "{{names}}",
            withoutGroup.map(ingredientNameOf).join(", "),
          ),
        );
      }
    }
    return errors;
  };

  const handleDrawerVisible = async (visible) => {
    if (visible) {
      setHasPersistedStockItem(false);
      loadAutomacaoTratos();
      loadStockItemData();
      formik.current.resetForm({
        ...INITIAL_FORM_SUPPLEMENT_VALUES,
        groupId,
        farmId,
        formulateItems: [],
      });
      if (idSupplement) {
        fetchDataSupplement();
      } else {
        setFormSupplement({
          ...INITIAL_FORM_SUPPLEMENT_VALUES,
          groupId,
          farmId,
          formulateItems: [],
        });
        setSustenanceCapacityData(INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES);
        setSupplementFarmRestrictionsData(
          INITIAL_FORM_FARM_RESTRICTIONS_VALUES,
        );
      }
    } else {
      formik.current.resetForm({
        ...INITIAL_FORM_SUPPLEMENT_VALUES,
        groupId,
        farmId,
      });
      setFormSupplement({ ...INITIAL_FORM_SUPPLEMENT_VALUES, groupId, farmId });
      setSustenanceCapacityData(INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES);
      setSupplementFarmRestrictionsData(INITIAL_FORM_FARM_RESTRICTIONS_VALUES);
      setIngredientAnalysis(null);
    }
  };

  const handleSave = useCallback(
    async (supplement, id = null) => {
      setIsLoadingRequestSaveOrUpdate(true);
      const body = {
        ...supplement,
        status: id === null ? "Active" : supplement.status,
      };
      try {
        await saveSupplement({
          groupId,
          farmId: body.farmId,
          id,
          supplement: body,
          isInputSupplier: false,
        });
        setIsLoadingRequestSaveOrUpdate(false);
        dispatch(
          SupplementActions.indexPageSupplement(
            groupId,
            farmId,
            supplementPagination.page,
            supplementPagination.sorter,
            supplementPagination.filters,
            supplementPagination.size,
            false,
            supplementPagination.tableFilters,
            supplementPagination.tableSorters,
          ),
        );
        if (id == null) {
          notification.success({
            message: translation.supplement.successSaveFormulated,
          });
        } else {
          notification.success({
            message: translation.supplement.successSaveFormulated,
          });
        }
        closeDrawer();
      } catch (error) {
        setIsLoadingRequestSaveOrUpdate(false);
        const backendMessage = (
          error?.response?.data?.results?.message ||
          error?.response?.data?.message ||
          ""
        ).toLowerCase();
        notification.error({
          message: backendMessage.includes("formulation nutrients do not match")
            ? translation.supplement.errorSaveFormulatedNutrientsMismatch
            : translation.supplement.errorSaveFormulated,
        });
      }
    },
    [
      closeDrawer,
      dispatch,
      farmId,
      groupId,
      supplementPagination.filters,
      supplementPagination.page,
      supplementPagination.size,
      supplementPagination.sorter,
      supplementPagination.tableFilters,
      supplementPagination.tableSorters,
      translation.supplement.errorSaveFormulated,
      translation.supplement.errorSaveFormulatedNutrientsMismatch,
      translation.supplement.successSaveFormulated,
    ],
  );

  const handleSubmitModalForm = async (values, actions) => {
    let sustenanceCapacityToBeSaveData = sustenanceCapacityData;

    if (values.groupId == null && values.farmId == null) {
      values.groupId = groupId;
      values.farmId = farmId;
    }
    const stockItemErrors = validateStockItem(values);
    if (stockItemErrors.length > 0) {
      openNotificationError(
        "Dados incompletos",
        <ul>
          {stockItemErrors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>,
      );
      return;
    }
    if (validateFormulateItems(values.formulateItems).size > 0) {
      openNotificationError(
        "Dados incompletos",
        <ul>
          {Array.from(validateFormulateItems(values.formulateItems)).map(
            (error, index) => (
              <li key={index}>{error}</li>
            ),
          )}
        </ul>,
      );
      return;
    }
    const hasAllRawMaterials = values.formulateItems.every(
      (item) => item.supplementOrRawMaterial,
    );
    const computedNutrients = hasAllRawMaterials
      ? computeFormulationNutrients(values.formulateItems)
      : null;
    const recalculatedNutrients = computedNutrients
      ? [...FORMULATION_NUTRIENTS, "dryMatterPercentage"].reduce(
          (acc, nutrient) => ({
            ...acc,
            [nutrient]: computedNutrients[nutrient],
          }),
          {},
        )
      : {};
    if (
      sustenanceCapacityToBeSaveData &&
      sustenanceCapacityToBeSaveData.status === "NI"
    ) {
      openNotificationError(
        "Dados incompletos",
        translation.supplement.errorNecessarySustenanceCapacity,
      );
      return;
    }

    const ingredientsToCreate = ingredientsToCreateOf(values);
    const ingredientsToRepoint = ingredientsToRepointOf(values);
    const body = {
      ...values,
      ...recalculatedNutrients,
      ...sustenanceCapacityData,
      status: values.status,
      restriction:
        supplementFarmRestrictionsData &&
        supplementFarmRestrictionsData.status === "I"
          ? {
              ...supplementFarmRestrictionsData,
            }
          : null,
      groupId: values.groupId,
      farmId: values.farmId,
      observations: values.observations ? values.observations.trim() : null,
      ingredientStockItemGroups: ingredientsToCreate.map((item) => ({
        ingredientId: item.ingredientId,
        groupItemId:
          values.ingredientStockItemGroups?.[item.ingredientId]?.groupItemId ??
          null,
        itemGroupId:
          values.ingredientStockItemGroups?.[item.ingredientId]?.itemGroupId ??
          null,
      })),
      formulateItems: values.formulateItems.map((item) => {
        item.id = null;
        return item;
      }),
    };
    const title = "Ao confirmar, os itens abaixo devem ser considerados:";
    const content = (
      <ul>
        {ingredientsToCreate.length > 0 && (
          <li style={{ marginBottom: 26, marginTop: 25 }}>
            <Bold>
              {translation.supplement.ingredientStockItemsWillBeCreated.replace(
                "{{count}}",
                ingredientsToCreate.length,
              )}
            </Bold>
            <ul>
              {ingredientsToCreate.map((item) => (
                <li key={item.ingredientId}>
                  {`${ingredientNameOf(item)} — ${groupItemNameOf(
                    values.ingredientStockItemGroups?.[item.ingredientId]
                      ?.groupItemId,
                  )} / ${itemGroupNameOf(
                    values.ingredientStockItemGroups?.[item.ingredientId]
                      ?.itemGroupId,
                  )}`}
                </li>
              ))}
            </ul>
          </li>
        )}
        {ingredientsToRepoint.length > 0 && (
          <li style={{ marginBottom: 26 }}>
            {translation.supplement.ingredientsWillUseFarmCopy}{" "}
            {ingredientsToRepoint.map(ingredientNameOf).join(", ")}
          </li>
        )}
        <li style={{ marginBottom: 26, marginTop: 25 }}>
          Atualize as informações de Suplemento/Concent. em todas as dietas em{" "}
          <Bold>Edição de "Estratégia de dieta", </Bold>
          caso contrário, todas as estratégias estarão considerando os valores
          antigos deste Suplemento/Concent.
        </li>
        <li style={{ marginBottom: 26 }}>
          Após finalizar o passo acima, <Bold>reprocesse</Bold> todas os
          Cenários de venda - <Bold>Favoritos</Bold> que utilizam as
          <Bold> estratégias acima como baseline,</Bold> caso contrário, todos
          os cenários estarão considerando os valores antigos deste
          Suplemento/Concent.
        </li>
        <li>
          Para os <Bold>Cenários de compra,</Bold> você deve atualizar a
          estratégia de dieta e reprocessar o cenário em{" "}
          <Bold>Edição de Cenário de compra</Bold>, caso contrário, todas as
          estratégias e cenários de compra estarão considerando os valores
          antigos deste Suplemento/Concent.
        </li>
      </ul>
    );
    if (body.id === null) {
      Modal.confirm({
        title,
        content,
        width: 580,
        icon: <Icon type="reload" />,
        centered: true,
        onOk: () => handleSave(body),
      });
    } else {
      Modal.confirm({
        title,
        content,
        width: 580,
        icon: <Icon type="reload" />,
        centered: true,
        onOk: () => handleSave(body, body.id),
      });
    }
  };

  /* Method will be use in button on SustenanceCapacity */
  const handleEditSustenanceCapacity = (
    supplement,
    sustenanceCapacityData,
    blockEdit,
  ) => {
    setSustenanceCapacityData((oldState) => ({
      ...oldState,
      showMix: supplement.type !== "Supplement",
    }));
    setDrawerSupplementSustenanceCapacityVisible(true);
  };

  const handleSaveSustenanceCapacity = (data) => {
    if (data != null) {
      setSustenanceCapacityData(data);
      setDrawerSupplementSustenanceCapacityVisible(false);
    }
  };

  const handleCancelSustenanceCapacity = () => {
    setDrawerSupplementSustenanceCapacityVisible(false);
  };

  /* Method will be use in button on Farm Restrictions */
  const handleEditFarmRestrictions = () => {
    setDrawerSupplementFarmRestrictionsVisible((oldState) => !oldState);
  };

  const handleSaveFarmRestrictionsData = (data) => {
    if (data != null) {
      setSupplementFarmRestrictionsData(data);
      setDrawerSupplementFarmRestrictionsVisible(false);
    }
  };

  const handleCancelFarmRestrictionsData = () => {
    setDrawerSupplementFarmRestrictionsVisible(false);
  };

  const handleComputeNutrientsFromFormulatedItems = useCallback(
    (formulateItems, setFieldValue) => {
      const computed = computeFormulationNutrients(formulateItems);
      FORMULATION_NUTRIENTS.forEach((nutrient) =>
        setFieldValue(nutrient, computed[nutrient]),
      );
      setFieldValue("cost", computed.cost);
      setFieldValue("dryMatterPercentage", computed.dryMatterPercentage);
    },
    [],
  );

  const handleAddNewRawMaterial = useCallback(
    (values, setFieldValue) => {
      const formulateItems = values.formulateItems || [];
      const nextOrder =
        formulateItems.length === 0
          ? 1
          : formulateItems.reduce(
              (max, item) => Math.max(max, item.inclusionOrder || 0),
              1,
            );
      const currentUnit =
        formulateItems.find((i) => i.amountUnit)?.amountUnit ||
        (isAutomacaoTratosEnabled
          ? RAW_MATERIAL_AMOUNT_UNIT.kg_mo.value
          : RAW_MATERIAL_AMOUNT_UNIT.percent_kg_ms.value);
      formulateItems.push({
        id: uuid(),
        supplementOrRawMaterialId: null,
        supplementOrRawMaterialName: null,
        supplementOrRawMaterial: null,
        amount: 0,
        amountUnit: currentUnit,
        originalMatterAmount: null,
        addedDryMatterAmount: null,
        baseDryMatterAmount: null,
        cost: 0,
        dryMatterPercentage: 0,
        inclusionOrder: nextOrder,
        mixingTime: null,
      });
      setFieldValue("formulateItems", formulateItems);
    },
    [isAutomacaoTratosEnabled],
  );

  const sumMixingTime = (items) => {
    const list = items ?? [];

    if (!list.some((item) => item.mixingTime != null)) {
      return null;
    }

    return list.reduce((total, item) => total + (item.mixingTime ?? 0), 0);
  };

  const handleChangeFormulateItems = useCallback(
    (field, data, id, values, setFieldValue) => {
      const formulateItems = values.formulateItems.map((item) => {
        if (item.id === id) {
          item[field] = data;
          return { ...item };
        }
        return item;
      });
      setFieldValue("formulateItems", formulateItems);
      if (
        field === "amount" ||
        field === "originalMatterAmount" ||
        field === "addedDryMatterAmount" ||
        field === "baseDryMatterAmount" ||
        field === "supplementOrRawMaterial"
      ) {
        handleComputeNutrientsFromFormulatedItems(
          formulateItems,
          setFieldValue,
        );
      }
      if (field === "supplementOrRawMaterialId") {
        analyzeIngredients(formulateItems);
      }
    },
    [handleComputeNutrientsFromFormulatedItems, analyzeIngredients],
  );

  const handleChangeAmountUnit = useCallback(
    (newUnit, recordId, values, setFieldValue) => {
      const currentItems = values.formulateItems || [];
      const changesAllItems =
        newUnit === KG_MO || isOriginalMatterFormulation(currentItems);
      const formulateItems = currentItems.map((item) => {
        if (!changesAllItems && item.id !== recordId) {
          return item;
        }
        return {
          ...item,
          amountUnit: newUnit,
          amount: newUnit === PERCENT_KG_MS ? item.amount : 0,
          originalMatterAmount:
            newUnit === KG_MO ? item.originalMatterAmount : null,
          addedDryMatterAmount:
            newUnit === KG_MS_PER_BASE_KG_MS ? item.addedDryMatterAmount : null,
          baseDryMatterAmount:
            newUnit === KG_MS_PER_BASE_KG_MS ? item.baseDryMatterAmount : null,
        };
      });
      setFieldValue("formulateItems", formulateItems);
      handleComputeNutrientsFromFormulatedItems(formulateItems, setFieldValue);
    },
    [handleComputeNutrientsFromFormulatedItems],
  );

  const handleDeleteRawMaterial = useCallback(
    (record, values, setFieldValue) => {
      const formulateItems = values.formulateItems.filter(
        (item) => item.id !== record.id,
      );
      setFieldValue("formulateItems", formulateItems);
      handleComputeNutrientsFromFormulatedItems(formulateItems, setFieldValue);
      analyzeIngredients(formulateItems);
    },
    [handleComputeNutrientsFromFormulatedItems, analyzeIngredients],
  );

  return (
    <Container
      title={
        formSupplement?.id != null
          ? translation.supplement.drawerSupplementFormulatedTitleEdit
          : translation.supplement.drawerSupplementFormulatedTitle
      }
      width={size.width > 768 ? size.width * 0.8 : "100%"}
      onClose={closeDrawer}
      maskClosable={false}
      visible={drawerCreateEditSupplementFormulatedVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Formik
        ref={formik}
        enableReinitialize={true}
        initialValues={formSupplement}
        onSubmit={handleSubmitModalForm}
        validationSchema={validationSchema}
        render={(props) => (
          <form onSubmit={props.handleSubmit} autoComplete="off">
            <Spin spinning={isLoadingRequestSaveOrUpdate}>
              <div className="drawerForm">
                <Row type="flex">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <span style={{ fontSize: "14px", color: "#000" }}>
                      {formSupplement?.id != null
                        ? translation.supplement
                            .drawerSupplementFormulatedSubTitleEdit
                        : translation.supplement
                            .drawerSupplementFormulatedSubTitle}
                    </span>
                  </Col>
                </Row>
                {/* Errors */}
                {Object.entries(props.errors).length > 0 &&
                  props.submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )}
                {/* Select Supplement Group Farm Visibility */}
                {formSupplement?.id == null && (
                  <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label>
                          {translation.supplement.form.groupFarmVisibility}
                        </label>
                      </Row>
                      <Row>
                        <RadioGroupCustom
                          value={
                            props.values.groupId != null &&
                            props.values.farmId != null
                              ? "farm"
                              : props.values.groupId != null &&
                                  props.values.farmId == null
                                ? "group"
                                : "farm"
                          }
                          buttonStyle="solid"
                        >
                          <RadioButtonCustom
                            value="farm"
                            onChange={(e) => {
                              props.setFieldValue("groupId", groupSelected.id);
                              props.setFieldValue("farmId", farmSelected.id);
                            }}
                          >
                            {translation.supplier.form.radiogroup.farm}
                          </RadioButtonCustom>
                          <RadioButtonCustom
                            value="group"
                            onChange={(e) => {
                              props.setFieldValue("groupId", groupSelected.id);
                              props.setFieldValue("farmId", null);
                            }}
                          >
                            {translation.supplement.form.radiogroup.group}:
                            {groupSelected.name}
                          </RadioButtonCustom>
                        </RadioGroupCustom>
                      </Row>
                    </Col>
                  </Row>
                )}
                {/* Supplement Group */}
                {/* Supplement Sub Group */}
                {/* Supplement Prensetation */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  justify="start"
                  gutter={8}
                >
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label
                        className={
                          props.errors.supplementGroupId &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.supplementGroupId}*
                        <InfoTooltip
                          title={translation.supplement.form.supplementTypeHelp}
                        />
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={props.values.supplementGroupId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) => {
                          props.setFieldValue("supplementGroupId", value);
                          props.setFieldValue("supplementSubGroupId", null);
                        }}
                      >
                        {SUPPLEMENT_GROUPS.map((group) => (
                          <Select.Option key={group.id} value={group.id}>
                            {group.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label
                        className={
                          props.errors.supplementSubGroupId &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.supplementSubGroupId}*
                        {/* <InfoTooltip
                        title={
                          <>
                            <ol style={{ marginLeft: 16 }}>
                              <li>
                                <strong>
                                  {
                                    translation.supplement.form
                                      .supplementClassificationHelp.title_0
                                  }
                                  :
                                </strong>
                                {
                                  translation.supplement.form
                                    .supplementClassificationHelp
                                    .description_0
                                }
                              </li>
                              <li>
                                <strong>
                                  {
                                    translation.supplement.form
                                      .supplementClassificationHelp.title_1
                                  }
                                  :
                                </strong>
                                {
                                  translation.supplement.form
                                    .supplementClassificationHelp
                                    .description_1
                                }
                              </li>
                              <li>
                                <strong>
                                  {
                                    translation.supplement.form
                                      .supplementClassificationHelp.title_2
                                  }
                                  :
                                </strong>
                                {
                                  translation.supplement.form
                                    .supplementClassificationHelp
                                    .description_2
                                }
                              </li>
                            </ol>
                            <p>
                              {
                                translation.supplement.form
                                  .supplementClassificationHelp.source
                              }
                            </p>
                          </>
                        }
                      /> */}
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={props.values.supplementSubGroupId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) =>
                          props.setFieldValue("supplementSubGroupId", value)
                        }
                      >
                        {SUPPLEMENT_SUB_GROUPS.filter(
                          (subgroup) =>
                            props.values.supplementGroupId &&
                            subgroup.supplement_group_id ===
                              props.values.supplementGroupId,
                        ).map((subgroup) => (
                          <Select.Option key={subgroup.id} value={subgroup.id}>
                            {subgroup.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label
                        className={
                          props.errors.supplementPresentationId &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.supplementPresentationId}*
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={
                          props.values.supplementPresentationId || undefined
                        }
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) =>
                          props.setFieldValue("supplementPresentationId", value)
                        }
                      >
                        {SUPPLEMENT_PRESENTATION.map((presentation) => (
                          <Select.Option
                            key={presentation.id}
                            value={presentation.id}
                          >
                            {presentation.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                </Row>
                {/* Input supplement identification */}
                {/* Import code */}
                {/* Cost */}
                {/* DRY MATTER PERCENTAGE */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  justify="start"
                  gutter={8}
                >
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    style={{ marginBottom: "13px" }}
                  >
                    <Row>
                      <label
                        className={
                          props.errors.name && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.name}*
                      </label>
                    </Row>
                    <Row>
                      <Input
                        placeholder={
                          translation.supplement.form.namePlaceHolder
                        }
                        value={props.values.name}
                        name="name"
                        autoComplete="chrome-off"
                        onChange={(e) => {
                          props.setFieldValue("name", e.target.value);
                        }}
                      />
                    </Row>
                  </Col>
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    style={{ marginBottom: "13px" }}
                  >
                    <Row>
                      <label>{translation.supplement.form.importCode}</label>
                    </Row>
                    <Row>
                      <Input
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.importCode}
                        name="importCode"
                        autoComplete="chrome-off"
                        onChange={(e) => {
                          props.setFieldValue("importCode", e.target.value);
                        }}
                      />
                    </Row>
                  </Col>
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    style={{ marginBottom: "13px" }}
                  >
                    <Row>
                      <label>
                        {translation.supplement.form.mixingTime}
                        <InfoTooltip
                          title={translation.supplement.form.mixingTimeHelp}
                        />
                      </label>
                    </Row>
                    <Row>
                      <Input
                        disabled
                        value={(() => {
                          const total =
                            sumMixingTime(props.values.formulateItems) ??
                            props.values.mixingTime;
                          return total == null ? "" : `${total} min`;
                        })()}
                      />
                    </Row>
                  </Col>
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    style={{ marginBottom: "13px" }}
                  >
                    <Row>
                      <label
                        className={
                          props.errors.cost && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.cost}*
                      </label>
                    </Row>
                    <Row>
                      <NumberFormat
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.costPlaceHolder
                        }
                        value={props.values.cost}
                        name="cost"
                        addonBefore="R$"
                        addonAfter="kg"
                        decimalScale={DECIMAL_SCALE_PRICE}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "cost",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 10000
                                  ? 10000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>

                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={8}
                    xl={8}
                    style={{ marginBottom: "13px" }}
                  >
                    <Row>
                      <label
                        className={
                          props.errors.dryMatterPercentage &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.dryMatterPercentage}*
                      </label>
                    </Row>
                    <Row>
                      <NumberFormat
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.dryMatterPercentage}
                        name="dryMatterPercentage"
                        min={0}
                        max={100}
                        addonAfter="%MS"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "dryMatterPercentage",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* Select Raw Material */}
                {/* Supplement Capacity & Farm Restrictions*/}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  justify="start"
                  gutter={8}
                >
                  {/* Select Raw Material */}
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomDivider dashed>Matéria(s) Prima(s)</CustomDivider>
                    {/* List Materials */}
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      justify="end"
                    >
                      <ButtonStandard
                        type="button"
                        buttonType="addNew"
                        onClick={() =>
                          handleAddNewRawMaterial(
                            props.values,
                            props.setFieldValue,
                          )
                        }
                      >
                        {translation.buttons.add}
                      </ButtonStandard>
                    </Row>
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      justify="start"
                    >
                      <Col span={24}>
                        <Table
                          dataSource={[
                            ...(props.values.formulateItems || []),
                          ].sort(
                            (a, b) =>
                              (a.inclusionOrder || 0) - (b.inclusionOrder || 0),
                          )}
                          rowKey={"id"}
                          pagination={false}
                          footer={(currentPageData) => {
                            if (isOriginalMatterFormulation(currentPageData)) {
                              const totalItems = currentPageData.reduce(
                                (acc, item) =>
                                  acc + (item.originalMatterAmount || 0),
                                0,
                              );
                              return (
                                <span>
                                  <strong>Total: </strong>
                                  {`${numberMask(totalItems, false)} ${
                                    RAW_MATERIAL_AMOUNT_UNIT[KG_MO].label
                                  }`}
                                </span>
                              );
                            }
                            const totalPercent =
                              getTotalPercentAmount(currentPageData);
                            return (
                              <span>
                                <strong>Total: </strong>
                                {`${numberMask(totalPercent, false)} ${
                                  RAW_MATERIAL_AMOUNT_UNIT[PERCENT_KG_MS].label
                                }`}
                              </span>
                            );
                          }}
                        >
                          <Table.Column
                            title={translation.supplement.form.inclusionOrder}
                            dataIndex="inclusionOrder"
                            width={110}
                            render={(text, record) => (
                              <InputNumber
                                value={record.inclusionOrder}
                                min={1}
                                precision={0}
                                onChange={(value) => {
                                  handleChangeFormulateItems(
                                    "inclusionOrder",
                                    value,
                                    record.id,
                                    props.values,
                                    props.setFieldValue,
                                  );
                                }}
                              />
                            )}
                          />
                          <Table.Column
                            title={translation.supplement.form.itemMixingTime}
                            dataIndex="mixingTime"
                            width={110}
                            render={(text, record) => (
                              <InputNumber
                                value={record.mixingTime}
                                placeholder="0"
                                min={0}
                                precision={0}
                                onChange={(value) => {
                                  handleChangeFormulateItems(
                                    "mixingTime",
                                    value,
                                    record.id,
                                    props.values,
                                    props.setFieldValue,
                                  );
                                }}
                              />
                            )}
                          />
                          <Table.Column
                            title="Mat. Prima"
                            dataIndex="supplementOrRawMaterialId"
                            render={(text, record) => {
                              return (
                                <SearchSupplement
                                  searchFormulationIngredients
                                  excludeSupplementId={props.values.id}
                                  textSearch={
                                    record.supplementOrRawMaterialName
                                  }
                                  setTextSearch={(value) =>
                                    handleChangeFormulateItems(
                                      "supplementOrRawMaterialName",
                                      value,
                                      record.id,
                                      props.values,
                                      props.setFieldValue,
                                    )
                                  }
                                  handleChange={(value, supplement) => {
                                    if (
                                      value != null &&
                                      value === props.values.id
                                    ) {
                                      openNotificationError(
                                        "Dados incompletos",
                                        translation.supplement
                                          .errorFormulatedItemWithItself,
                                      );
                                      return;
                                    }
                                    handleChangeFormulateItems(
                                      "supplementOrRawMaterialId",
                                      value,
                                      record.id,
                                      props.values,
                                      props.setFieldValue,
                                    );
                                    if (supplement && supplement.versions) {
                                      handleChangeFormulateItems(
                                        "supplementOrRawMaterial",
                                        supplement.versions[
                                          supplement.versions.length - 1
                                        ],
                                        record.id,
                                        props.values,
                                        props.setFieldValue,
                                      );
                                      handleChangeFormulateItems(
                                        "cost",
                                        supplement.cost || 0,
                                        record.id,
                                        props.values,
                                        props.setFieldValue,
                                      );
                                    }
                                  }}
                                />
                              );
                            }}
                          />
                          <Table.Column
                            title="Qtd"
                            render={(text, record) =>
                              record.amountUnit === KG_MO ? (
                                <NumberFormat
                                  customInput={Input}
                                  value={record.originalMatterAmount}
                                  placeholder="0"
                                  decimalScale={2}
                                  decimalSeparator=","
                                  thousandSeparator="."
                                  allowNegative={false}
                                  onValueChange={({ floatValue }) => {
                                    handleChangeFormulateItems(
                                      "originalMatterAmount",
                                      floatValue == null ? null : floatValue,
                                      record.id,
                                      props.values,
                                      props.setFieldValue,
                                    );
                                  }}
                                />
                              ) : record.amountUnit === KG_MS_PER_BASE_KG_MS ? (
                                <NumberFormat
                                  customInput={Input}
                                  value={record.addedDryMatterAmount}
                                  placeholder="0"
                                  decimalScale={2}
                                  decimalSeparator=","
                                  thousandSeparator="."
                                  allowNegative={false}
                                  onValueChange={({ floatValue }) => {
                                    handleChangeFormulateItems(
                                      "addedDryMatterAmount",
                                      floatValue == null ? null : floatValue,
                                      record.id,
                                      props.values,
                                      props.setFieldValue,
                                    );
                                  }}
                                />
                              ) : (
                                <InputNumber
                                  value={record.amount}
                                  min={0}
                                  max={100}
                                  onChange={(value) => {
                                    handleChangeFormulateItems(
                                      "amount",
                                      value,
                                      record.id,
                                      props.values,
                                      props.setFieldValue,
                                    );
                                  }}
                                />
                              )
                            }
                          />
                          <Table.Column
                            title={translation.supplement.form.baseAmount}
                            width={130}
                            render={(text, record) =>
                              record.amountUnit === KG_MS_PER_BASE_KG_MS ? (
                                <NumberFormat
                                  customInput={Input}
                                  value={record.baseDryMatterAmount}
                                  placeholder="0"
                                  decimalScale={2}
                                  decimalSeparator=","
                                  thousandSeparator="."
                                  allowNegative={false}
                                  onValueChange={({ floatValue }) => {
                                    handleChangeFormulateItems(
                                      "baseDryMatterAmount",
                                      floatValue == null ? null : floatValue,
                                      record.id,
                                      props.values,
                                      props.setFieldValue,
                                    );
                                  }}
                                />
                              ) : null
                            }
                          />
                          <Table.Column
                            title="Unidade"
                            dataIndex="amountUnit"
                            render={(text, record) => (
                              <Select
                                value={text}
                                style={{ width: 140 }}
                                onChange={(value) =>
                                  handleChangeAmountUnit(
                                    value,
                                    record.id,
                                    props.values,
                                    props.setFieldValue,
                                  )
                                }
                              >
                                {Object.values(RAW_MATERIAL_AMOUNT_UNIT)
                                  .filter(
                                    (u) =>
                                      u.value !== KG_MO ||
                                      isAutomacaoTratosEnabled,
                                  )
                                  .map((u) => (
                                    <Select.Option
                                      key={u.value}
                                      value={u.value}
                                    >
                                      {u.label}
                                    </Select.Option>
                                  ))}
                              </Select>
                            )}
                          />
                          <Table.Column
                            render={(text, record) => (
                              <button
                                type="button"
                                style={{
                                  all: "unset",
                                }}
                                onClick={() =>
                                  handleDeleteRawMaterial(
                                    record,
                                    props.values,
                                    props.setFieldValue,
                                  )
                                }
                              >
                                <TrashIcon />
                              </button>
                            )}
                          />
                        </Table>
                      </Col>
                    </Row>
                    {/* TDN AND CP */}
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      align="middle"
                    >
                      {/* TDN */}
                      <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Row>
                          <label
                            className={
                              props.errors.tdn && props.submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {translation.supplement.form.tdn}*
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.tdnPlaceHolder
                            }
                            value={props.values.tdn}
                            name="tdn"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      {/* CP */}
                      <Col xs={11} sm={11} md={11} lg={11} xl={11} offset={1}>
                        <Row>
                          <label
                            className={
                              props.errors.cp && props.submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {translation.supplement.form.cp}*
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.cpPlaceHolder
                            }
                            value={props.values.cp}
                            name="cp"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                    </Row>
                    {/* CALCIUM, PHOSPHORUS AND NITROGEN */}
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      gutter={8}
                      align="middle"
                    >
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>{translation.supplement.form.calcium}</label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.calciumPlaceHolder
                            }
                            value={props.values.calcium}
                            name="calcium"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.phosphorus}
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.phosphorusPlaceHolder
                            }
                            value={props.values.phosphorus}
                            name="phosphorus"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>{translation.supplement.form.nitrogen}</label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.nitrogenPlaceHolder
                            }
                            value={props.values.nitrogen}
                            name="nitrogen"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                    </Row>
                    {/* ZINC, SODIUM, POTASSIUM, SULFUR  */}
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      gutter={8}
                      align="middle"
                    >
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>{translation.supplement.form.zinc}</label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.zincPlaceHolder
                            }
                            value={props.values.zinc}
                            name="zinc"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>{translation.supplement.form.sodium}</label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form.sodiumPlaceHolder
                            }
                            value={props.values.sodium}
                            name="sodium"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>{translation.supplement.form.potassium}</label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={props.values.potassium}
                            name="potassium"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>{translation.supplement.form.sulfur}</label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={props.values.sulfur}
                            name="sulfur"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                          />
                        </Row>
                      </Col>
                      <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.additiveEffect}
                            <InfoTooltip
                              title={
                                translation.supplement.form.additiveEffectHelp
                              }
                            />
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <NumberFormat
                            disabled={true}
                            customInput={Input}
                            placeholder={
                              translation.supplement.form
                                .additiveEffectPlaceHolder
                            }
                            value={props.values.additiveEffect}
                            name="additiveEffect"
                            addonAfter={"g/kg"}
                            decimalScale={DECIMAL_SCALE_NUTRIENTS}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            maxLength={6}
                          />
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomDivider dashed>
                      {translation.supplement.form.capacityDivider}
                    </CustomDivider>
                    <Row style={{ marginTop: "13px" }}>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <TableCustomSupplement>
                          <tbody>
                            <tr>
                              <td>
                                <span>
                                  {
                                    translation.supplement.form
                                      .sustenanceCapacity
                                  }
                                  *
                                </span>
                              </td>
                              <td>
                                {sustenanceCapacityData.status === "NI" ? (
                                  <span>
                                    <AlertIcon />{" "}
                                    {translation.status.pendingData}
                                  </span>
                                ) : (
                                  <span className="success">
                                    <SuccessIcon />{" "}
                                    {translation.status.informedData}
                                  </span>
                                )}
                              </td>
                              <td>
                                {sustenanceCapacityData.status === "NI" && (
                                  <button
                                    className="add"
                                    type="button"
                                    onClick={() =>
                                      handleEditSustenanceCapacity(
                                        props.values,
                                        sustenanceCapacityData,
                                      )
                                    }
                                  >
                                    {translation.buttons.addInfo}
                                    <PlusBlueIcon />
                                  </button>
                                )}
                                {sustenanceCapacityData.status === "I" && (
                                  <button
                                    className="edit"
                                    type="button"
                                    style={{ marginLeft: "60px" }}
                                    onClick={() =>
                                      handleEditSustenanceCapacity(
                                        props.values,
                                        sustenanceCapacityData,
                                      )
                                    }
                                  >
                                    {translation.buttons.edit}
                                    <PencilIcon />
                                  </button>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </TableCustomSupplement>
                      </Col>
                    </Row>
                    <CustomDivider dashed>
                      {translation.supplement.form.restrictionsDivider}
                    </CustomDivider>
                    <Row style={{ marginTop: "13px" }}>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <TableCustomSupplement>
                          <tbody>
                            <tr>
                              <td>
                                <span>
                                  {translation.supplement.form.restrictions}
                                </span>
                              </td>
                              <td>
                                {supplementFarmRestrictionsData.status ===
                                "NI" ? (
                                  <span>
                                    <AlertIcon />{" "}
                                    {translation.status.pendingData}
                                  </span>
                                ) : (
                                  <span className="success">
                                    <SuccessIcon />{" "}
                                    {translation.status.informedData}
                                  </span>
                                )}
                              </td>
                              <td>
                                {supplementFarmRestrictionsData.status ===
                                  "NI" && (
                                  <button
                                    className="add"
                                    type="button"
                                    onClick={handleEditFarmRestrictions}
                                  >
                                    {translation.buttons.addInfo}
                                    <PlusBlueIcon />
                                  </button>
                                )}
                                {supplementFarmRestrictionsData.status ===
                                  "I" && (
                                  <button
                                    className="edit"
                                    type="button"
                                    style={{ marginLeft: "60px" }}
                                    onClick={handleEditFarmRestrictions}
                                  >
                                    {translation.buttons.edit}
                                    <PencilIcon />
                                  </button>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </TableCustomSupplement>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <CustomDivider dashed>
                  {translation.supplement.form.stockItemsDivider}
                </CustomDivider>
                {props.values.farmId == null && (
                  <Row type="flex" style={{ marginTop: "13px" }}>
                    <Col span={24}>
                      <span style={{ color: "#D44C4C", fontSize: "12px" }}>
                        {translation.supplement.form.stockItemGroupRecipeHelp}
                      </span>
                    </Col>
                  </Row>
                )}
                {props.values.farmId != null && (
                  <StockItemSubsection>
                    <span className="title">
                      {translation.supplement.form.rationStockItemTitle}
                    </span>
                    <span className="help">
                      {translation.supplement.form.rationStockItemHelp}
                    </span>
                  </StockItemSubsection>
                )}
                {hasPersistedStockItem && props.values.farmId != null && (
                  <Row type="flex" style={{ marginTop: "13px" }}>
                    <Col span={24}>
                      <Row>
                        <label>{translation.supplement.form.stockItem}</label>
                        <InfoTooltip
                          title={translation.supplement.form.stockItemEditHelp}
                        />
                      </Row>
                      <Row className="rowInput">
                        <span className="rowInputData">
                          {stockItemLabel(props.values.stockItemId)}
                        </span>
                      </Row>
                    </Col>
                  </Row>
                )}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  gutter={8}
                  align="bottom"
                  hidden={hasPersistedStockItem || props.values.farmId == null}
                >
                  <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                    <Row>
                      <label>
                        {translation.supplement.form.createStockItem}
                      </label>
                      <InfoTooltip
                        title={translation.supplement.form.createStockItemHelp}
                      />
                    </Row>
                    <Row className="rowInput">
                      <RadioGroupCustom
                        value={props.values.createStockItem}
                        onChange={(e) =>
                          props.setFieldValue("createStockItem", e.target.value)
                        }
                      >
                        <RadioButtonCustom value={true}>
                          {translation.buttons.yes}
                        </RadioButtonCustom>
                        <RadioButtonCustom value={false}>
                          {translation.buttons.no}
                        </RadioButtonCustom>
                      </RadioGroupCustom>
                    </Row>
                  </Col>
                  {props.values.createStockItem && (
                    <>
                      <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.stockItemInternalId}*
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <Input
                            value={props.values.stockItemInternalId}
                            onChange={(e) =>
                              props.setFieldValue(
                                "stockItemInternalId",
                                e.target.value,
                              )
                            }
                          />
                        </Row>
                      </Col>
                      <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.stockItemAbbreviation}
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <Input
                            value={props.values.stockItemAbbreviation}
                            onChange={(e) =>
                              props.setFieldValue(
                                "stockItemAbbreviation",
                                e.target.value,
                              )
                            }
                          />
                        </Row>
                      </Col>
                      <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.stockItemUnit}*
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <Select
                            placeholder={translation.defaultSelectPlaceholder}
                            value={
                              props.values.stockItemMeasurementUnitId ||
                              undefined
                            }
                            style={{ width: "100%" }}
                            onChange={(value) =>
                              props.setFieldValue(
                                "stockItemMeasurementUnitId",
                                value,
                              )
                            }
                          >
                            {units.map((unit) => (
                              <Select.Option key={unit.id} value={unit.id}>
                                {unit.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Row>
                      </Col>
                      <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.stockItemGroupItem}*
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <Select
                            placeholder={translation.defaultSelectPlaceholder}
                            value={
                              props.values.stockItemGroupItemId || undefined
                            }
                            style={{ width: "100%" }}
                            onChange={(value) => {
                              props.setFieldValue(
                                "stockItemGroupItemId",
                                value,
                              );
                              props.setFieldValue("stockItemItemGroupId", null);
                            }}
                          >
                            {groupItems.map((groupItem) => (
                              <Select.Option
                                key={groupItem.id}
                                value={groupItem.id}
                              >
                                {groupItem.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Row>
                      </Col>
                      <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Row>
                          <label>
                            {translation.supplement.form.stockItemItemGroup}*
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <Select
                            placeholder={translation.defaultSelectPlaceholder}
                            value={
                              props.values.stockItemItemGroupId || undefined
                            }
                            style={{ width: "100%" }}
                            disabled={!props.values.stockItemGroupItemId}
                            onChange={(value) =>
                              props.setFieldValue("stockItemItemGroupId", value)
                            }
                          >
                            {itemsGroups
                              .filter(
                                (itemsGroup) =>
                                  itemsGroup.groupItemId ===
                                  props.values.stockItemGroupItemId,
                              )
                              .map((itemsGroup) => (
                                <Select.Option
                                  key={itemsGroup.id}
                                  value={itemsGroup.id}
                                >
                                  {itemsGroup.name}
                                </Select.Option>
                              ))}
                          </Select>
                        </Row>
                      </Col>
                    </>
                  )}
                  {!props.values.createStockItem && (
                    <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                      <Row>
                        <label>{translation.supplement.form.stockItem}*</label>
                      </Row>
                      <Row className="rowInput">
                        <Select
                          placeholder={translation.defaultSelectPlaceholder}
                          value={props.values.stockItemId || undefined}
                          style={{ width: "100%" }}
                          onChange={(value) =>
                            props.setFieldValue("stockItemId", value)
                          }
                        >
                          {unlinkedStockItems.map((stockItem) => (
                            <Select.Option
                              key={stockItem.id}
                              value={stockItem.id}
                            >
                              {`${stockItem.internalId} - ${stockItem.description}`}
                            </Select.Option>
                          ))}
                        </Select>
                      </Row>
                    </Col>
                  )}
                </Row>
                {ingredientsToCreateOf(props.values).length > 0 && (
                  <StockItemSubsection>
                    <span className="title">
                      {translation.supplement.form.ingredientsStockItemsTitle}
                    </span>
                    <span className="help">
                      {translation.supplement.form.ingredientsStockItemsHelp}
                    </span>
                  </StockItemSubsection>
                )}
                {ingredientsToCreateOf(props.values).length > 0 && (
                  <Row type="flex" gutter={8} style={{ marginTop: "13px" }}>
                    <Col span={24}>
                      <Alert
                        type="warning"
                        showIcon
                        style={{
                          backgroundColor: "#FFFFFF",
                          borderColor: "#FAAD14",
                        }}
                        message={
                          translation.supplement.ingredientStockItemsBlockTitle
                        }
                        description={
                          <>
                            <div style={{ marginBottom: 8 }}>
                              {translation.supplement.ingredientStockItemsBlockHelp.replace(
                                "{{names}}",
                                ingredientsToCreateOf(props.values)
                                  .map(ingredientNameOf)
                                  .join(", "),
                              )}
                            </div>
                            <Table
                              rowKey="ingredientId"
                              size="small"
                              pagination={false}
                              dataSource={ingredientsToCreateOf(props.values)}
                            >
                              <Table.Column
                                title={translation.supplement.form.ingredient}
                                render={(text, record) =>
                                  ingredientNameOf(record)
                                }
                              />
                              <Table.Column
                                title={`${translation.supplement.form.stockItemGroupItem}*`}
                                width={220}
                                render={(text, record) => (
                                  <Select
                                    placeholder={
                                      translation.defaultSelectPlaceholder
                                    }
                                    value={
                                      props.values.ingredientStockItemGroups?.[
                                        record.ingredientId
                                      ]?.groupItemId || undefined
                                    }
                                    style={{ width: "100%" }}
                                    onChange={(value) =>
                                      setIngredientStockItemGroup(
                                        record.ingredientId,
                                        "groupItemId",
                                        value,
                                        props.values,
                                        props.setFieldValue,
                                      )
                                    }
                                  >
                                    {groupItems.map((groupItem) => (
                                      <Select.Option
                                        key={groupItem.id}
                                        value={groupItem.id}
                                      >
                                        {groupItem.name}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                )}
                              />
                              <Table.Column
                                title={`${translation.supplement.form.stockItemItemGroup}*`}
                                width={220}
                                render={(text, record) => {
                                  const groupItemId =
                                    props.values.ingredientStockItemGroups?.[
                                      record.ingredientId
                                    ]?.groupItemId;

                                  return (
                                    <Select
                                      placeholder={
                                        translation.defaultSelectPlaceholder
                                      }
                                      value={
                                        props.values
                                          .ingredientStockItemGroups?.[
                                          record.ingredientId
                                        ]?.itemGroupId || undefined
                                      }
                                      style={{ width: "100%" }}
                                      disabled={!groupItemId}
                                      onChange={(value) =>
                                        setIngredientStockItemGroup(
                                          record.ingredientId,
                                          "itemGroupId",
                                          value,
                                          props.values,
                                          props.setFieldValue,
                                        )
                                      }
                                    >
                                      {itemsGroups
                                        .filter(
                                          (itemsGroup) =>
                                            itemsGroup.groupItemId ===
                                            groupItemId,
                                        )
                                        .map((itemsGroup) => (
                                          <Select.Option
                                            key={itemsGroup.id}
                                            value={itemsGroup.id}
                                          >
                                            {itemsGroup.name}
                                          </Select.Option>
                                        ))}
                                    </Select>
                                  );
                                }}
                              />
                            </Table>
                          </>
                        }
                      />
                    </Col>
                  </Row>
                )}
                <Row type="flex" style={{ marginTop: "13px" }}>
                  <Col span={24}>
                    <Row>
                      <label>{translation.supplement.form.observations}</label>
                    </Row>
                    <Row className="rowInput">
                      <Input.TextArea
                        name="observations"
                        autoSize={{ minRows: 3, maxRows: 5 }}
                        value={props.values.observations}
                        placeholder={
                          translation.supplement.form.observationsPlaceHolder
                        }
                        onChange={(e) =>
                          props.setFieldValue("observations", e.target.value)
                        }
                      />
                    </Row>
                  </Col>
                </Row>
                <SupplementInfoCard>
                  <NoteIcon /> {translation.supplement.form.information}
                </SupplementInfoCard>
              </div>
              {/* Footer */}
              <Footer>
                <Row type="flex" justify="space-between">
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="selectDiv"
                  ></Col>
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="buttonsDiv"
                  >
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={closeDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>

                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </Spin>
          </form>
        )}
      />
      <DrawerSupplementSustenanceCapacity
        drawerVisible={drawerSupplementSustenanceCapacityVisible}
        data={sustenanceCapacityData}
        onSave={handleSaveSustenanceCapacity}
        onCancel={handleCancelSustenanceCapacity}
      />
      <DrawerSupplementRestrictions
        drawerVisible={drawerSupplementFarmRestrictionsVisible}
        data={supplementFarmRestrictionsData}
        onSave={handleSaveFarmRestrictionsData}
        onCancel={handleCancelFarmRestrictionsData}
      />
    </Container>
  );
}

export default DrawerSupplementFormulated;
